import React, { useState, useEffect } from "react";
import {
  useNavigate,
  Link,
  Outlet,
  useLocation,
  Navigate,
} from "react-router-dom";
import { profileService } from "../services/profile"; // Importar profileService
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import toast from "react-hot-toast";
// Imports do React Bootstrap para o menu responsivo
import { Offcanvas, Button } from "react-bootstrap";
import packageJson from "../../package.json";
import {
  faHome,
  faBox,
  faIndustry,
  faTruck,
  faChartBar,
  faClipboardList,
  faUserPlus,
  faBell,
  faUser,
  faSignOutAlt,
  faBars, // Ícone do menu hambúrguer
  faUsers, // Ícone para clientes
  faTerminal, // Ícone para dev console
  faCalculator, // Ícone para fórmulas de produção
  faRepeat, // Ícone para encomendas recorrentes
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../contexts/AuthContext"; // Importa useAuth
import { isSuperAdmin } from "./SuperAdminRoute";

const Layout = () => {
  const { user, isAuthenticated, isLoading, logout, getCurrentUserRole } =
    useAuth();
  const [userProfile, setUserProfile] = useState(null); // Novo estado para o perfil
  const [userRole, setUserRole] = useState(null); // Estado para armazenar a role do usuário

  // Estados para o menu responsivo
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  const handleCloseOffcanvas = () => setShowOffcanvas(false);
  const handleShowOffcanvas = () => setShowOffcanvas(true);

  const navigate = useNavigate();
  const location = useLocation(); // Hook para obter a localização atual

  useEffect(() => {
    // Se não estiver autenticado e não estiver carregando, redireciona para o login
    if (!isLoading && !isAuthenticated) {
      navigate("/");
    }
    // Comentado temporariamente para depuração do erro 406 no Supabase RLS
    // if (user && !userProfile) {
    //   profileService.getProfile(user.id).then(profile => {
    //     setUserProfile(profile);
    //   }).catch(error => {
    //     console.error("Erro ao buscar perfil:", error);
    //     toast.error("Erro ao carregar perfil do usuário.");
    //   });
    // }
  }, [isAuthenticated, isLoading, navigate, user, userProfile]);

  // Effect para buscar a role do usuário
  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        try {
          const role = await getCurrentUserRole();
          console.log("User role fetched:", role);
          setUserRole(role);
        } catch (error) {
          console.error("Erro ao buscar role do usuário:", error);
          setUserRole("user"); // Default role
        }
      }
    };

    fetchUserRole();
  }, [user, getCurrentUserRole]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    // Set initial value on client side
    setIsMobile(window.innerWidth < 640);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    logout(); // Usa a função de logout do AuthContext
    navigate("/");
  };

  if (isLoading) {
    return null; // O App.js já exibe o spinner de carregamento
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />; // Redireciona para o login se não estiver autenticado
  }

  // Utilizadores super-admin que NÃO são gustavo só podem aceder a /recurring-admin
  const isRestrictedSuperAdmin =
    isSuperAdmin(user) && user?.email?.toLowerCase() !== 'gustavo.vieira@hendrios.agency';
  if (isRestrictedSuperAdmin && location.pathname !== '/recurring-admin') {
    return <Navigate to="/recurring-admin" replace />;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Sidebar - visível apenas em telas grandes */}
      <aside
        id="sidebar"
        className="fixed left-0 top-16 w-64 h-[calc(100vh-64px)] bg-white border-r border-neutral-200 flex flex-col hidden lg:block z-40"
      >
        {/* Logo da empresa removida da sidebar */}

        <nav className="flex-1 px-4 overflow-y-auto">
          <ul className="space-y-1">
            <li>
              <Link
                to="/dashboard"
                className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                  location.pathname === "/dashboard"
                    ? "bg-neutral-100 text-neutral-900"
                    : "text-neutral-600 hover:bg-neutral-50"
                }`}
              >
                <FontAwesomeIcon icon={faHome} className="w-5 h-5 mr-3" />
                Painel de Controlo
              </Link>
            </li>
            <li>
              <Link
                to="/stock"
                className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                  location.pathname === "/stock"
                    ? "bg-neutral-100 text-neutral-900"
                    : "text-neutral-600 hover:bg-neutral-50"
                }`}
              >
                <FontAwesomeIcon icon={faBox} className="w-5 h-5 mr-3" />
                Stock
              </Link>
            </li>
            <li>
              <Link
                to="/production"
                className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                  location.pathname === "/production"
                    ? "bg-neutral-100 text-neutral-900"
                    : "text-neutral-600 hover:bg-neutral-50"
                }`}
              >
                <FontAwesomeIcon icon={faIndustry} className="w-5 h-5 mr-3" />
                Produção
              </Link>
            </li>
            <li>
              <Link
                to="/expedition"
                className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                  location.pathname === "/expedition"
                    ? "bg-neutral-100 text-neutral-900"
                    : "text-neutral-600 hover:bg-neutral-50"
                }`}
              >
                <FontAwesomeIcon icon={faTruck} className="w-5 h-5 mr-3" />
                Expedição
              </Link>
            </li>
            <li>
              <Link
                to="/reports"
                className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                  location.pathname === "/reports"
                    ? "bg-neutral-100 text-neutral-900"
                    : "text-neutral-600 hover:bg-neutral-50"
                }`}
              >
                <FontAwesomeIcon icon={faChartBar} className="w-5 h-5 mr-3" />
                Relatórios
              </Link>
            </li>
            <li>
              <Link
                to="/orders"
                className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                  location.pathname === "/orders"
                    ? "bg-neutral-100 text-neutral-900"
                    : "text-neutral-600 hover:bg-neutral-50"
                }`}
              >
                <FontAwesomeIcon
                  icon={faClipboardList}
                  className="w-5 h-5 mr-3"
                />
                Encomendas
              </Link>
            </li>
            <li>
              <Link
                to="/profile"
                className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                  location.pathname === "/profile"
                    ? "bg-neutral-100 text-neutral-900"
                    : "text-neutral-600 hover:bg-neutral-50"
                }`}
              >
                <FontAwesomeIcon icon={faUser} className="w-5 h-5 mr-3" />
                Meu Perfil
              </Link>
            </li>
            {(userRole === "admin" || isSuperAdmin(user)) && (
              <li>
                <Link
                  to="/recurring-admin"
                  className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                    location.pathname === "/recurring-admin"
                      ? "bg-amber-100 text-amber-900"
                      : "text-amber-600 hover:bg-amber-50"
                  }`}
                >
                  <FontAwesomeIcon icon={faRepeat} className="w-5 h-5 mr-3" />
                  Encomendas Recorrentes
                </Link>
              </li>
            )}
            {userRole === "admin" && (
              <>
                <li>
                  <Link
                    to="/create-user"
                    className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                      location.pathname === "/create-user"
                        ? "bg-neutral-100 text-neutral-900"
                        : "text-neutral-600 hover:bg-neutral-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faUserPlus} className="w-5 h-5 mr-3" />
                    Gerir Utilizadores
                  </Link>
                </li>
                <li>
                  <Link
                    to="/clients"
                    className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                      location.pathname === "/clients" || location.pathname.startsWith("/client")
                        ? "bg-neutral-100 text-neutral-900"
                        : "text-neutral-600 hover:bg-neutral-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faUsers} className="w-5 h-5 mr-3" />
                    Gerir Clientes
                  </Link>
                </li>
                <li>
                  <Link
                    to="/production-formulas"
                    className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                      location.pathname === "/production-formulas"
                        ? "bg-neutral-100 text-neutral-900"
                        : "text-neutral-600 hover:bg-neutral-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faCalculator} className="w-5 h-5 mr-3" />
                    Gerir Fórmulas
                  </Link>
                </li>
                {process.env.NODE_ENV === "development" && (
                  <li>
                    <Link
                      to="/dev"
                      className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                        location.pathname === "/dev"
                          ? "bg-blue-100 text-blue-900"
                          : "text-blue-600 hover:bg-blue-50"
                      }`}
                    >
                      <FontAwesomeIcon icon={faTerminal} className="w-5 h-5 mr-3" />
                      Dev Console
                    </Link>
                  </li>
                )}
              </>
            )}
          </ul>
        </nav>

        <div className="pt-2 px-3 pb-3 border-t border-neutral-200 mt-auto">
          <div className="flex items-center mb-2 px-1">
            <img
              src={
                userProfile?.avatar_url ||
                "https://api.dicebear.com/7.x/notionists/svg?seed=1"
              }
              alt="User Avatar"
              className="w-8 h-8 rounded-full mr-3 object-cover border-2 border-neutral-100"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate">
                {user?.email}
              </p>
              <p className="text-xs text-neutral-500">
                {userRole === "admin" ? "Admin" : "Supervisor"}
              </p>
            </div>
          </div>
          <div className="mb-2 px-1">
            <p className="text-xs text-neutral-500">
              Versão: {packageJson.version}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center text-sm font-medium"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="mr-2 text-xs" />
            Sair
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pt-16 lg:pl-64">
        {" "}
        {/* Ajustado para considerar o header fixo e sidebar */}
        <header className="bg-white border-b border-neutral-200 fixed w-full z-50 top-0 left-0 lg:pl-64">
          {" "}
          {/* Header fixo - padding-left 0 para mobile, 64 para desktop */}
          <div className="px-2 sm:px-4 flex items-center justify-between h-16">
            <div className="flex items-center">
              {/* Botão de menu hambúrguer para mobile */}
              <Button
                variant="link"
                onClick={handleShowOffcanvas}
                className="p-2 mr-1 block lg:!hidden"
                style={{
                  display:
                    window.innerWidth >= 1024 ? "none !important" : "block",
                }}
              >
                <FontAwesomeIcon icon={faBars} className="text-neutral-600" />
              </Button>
              <img
                src="https://i.imgur.com/cRpaPnC.jpeg"
                alt="Logo"
                className="h-10 w-auto ml-1 sm:ml-0"
              />{" "}
              {/* Logo da empresa */}
            </div>
            <div className="flex items-center space-x-4">
              <div className="p-2 rounded-full opacity-60">
                <FontAwesomeIcon
                  icon={faChartBar}
                  className="text-neutral-600"
                />
              </div>
              <div className="p-2 rounded-full relative opacity-60">
                <FontAwesomeIcon icon={faBell} className="text-neutral-600" />
                <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                  !
                </span>
              </div>
              <div className="p-2 rounded-full relative opacity-60">
                <FontAwesomeIcon icon={faUser} className="text-neutral-600" />
                <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                  !
                </span>
              </div>
            </div>
          </div>
        </header>
        <div className="p-4">
          {console.log("Layout: Rendering Outlet content.")}
          <Outlet />
        </div>
      </main>

      {/* Offcanvas para navegação em dispositivos móveis */}
      <Offcanvas
        show={showOffcanvas}
        onHide={handleCloseOffcanvas}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Menu</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          {/* Replicando a navegação da sidebar dentro do Offcanvas */}
          <nav className="flex-1 px-4 overflow-y-auto max-h-[calc(100vh-196px)]">
            <ul className="space-y-1">
              <li>
                <Link
                  to="/dashboard"
                  onClick={handleCloseOffcanvas} // Fecha o offcanvas ao clicar
                  className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                    location.pathname === "/dashboard"
                      ? "bg-neutral-100 text-neutral-900"
                      : "text-neutral-600 hover:bg-neutral-50"
                  }`}
                >
                  <FontAwesomeIcon icon={faHome} className="w-5 h-5 mr-3" />
                  Painel de Controlo
                </Link>
              </li>
              <li>
                <Link
                  to="/stock"
                  onClick={handleCloseOffcanvas}
                  className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                    location.pathname === "/stock"
                      ? "bg-neutral-100 text-neutral-900"
                      : "text-neutral-600 hover:bg-neutral-50"
                  }`}
                >
                  <FontAwesomeIcon icon={faBox} className="w-5 h-5 mr-3" />
                  Stock
                </Link>
              </li>
              <li>
                <Link
                  to="/production"
                  onClick={handleCloseOffcanvas}
                  className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                    location.pathname === "/production"
                      ? "bg-neutral-100 text-neutral-900"
                      : "text-neutral-600 hover:bg-neutral-50"
                  }`}
                >
                  <FontAwesomeIcon icon={faIndustry} className="w-5 h-5 mr-3" />
                  Produção
                </Link>
              </li>
              <li>
                <Link
                  to="/expedition"
                  onClick={handleCloseOffcanvas}
                  className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                    location.pathname === "/expedition"
                      ? "bg-neutral-100 text-neutral-900"
                      : "text-neutral-600 hover:bg-neutral-50"
                  }`}
                >
                  <FontAwesomeIcon icon={faTruck} className="w-5 h-5 mr-3" />
                  Expedição
                </Link>
              </li>
              <li>
                <Link
                  to="/reports"
                  onClick={handleCloseOffcanvas}
                  className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                    location.pathname === "/reports"
                      ? "bg-neutral-100 text-neutral-900"
                      : "text-neutral-600 hover:bg-neutral-50"
                  }`}
                >
                  <FontAwesomeIcon icon={faChartBar} className="w-5 h-5 mr-3" />
                  Relatórios
                </Link>
              </li>
              <li>
                <Link
                  to="/orders"
                  onClick={handleCloseOffcanvas}
                  className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                    location.pathname === "/orders"
                      ? "bg-neutral-100 text-neutral-900"
                      : "text-neutral-600 hover:bg-neutral-50"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faClipboardList}
                    className="w-5 h-5 mr-3"
                  />
                  Encomendas
                </Link>
              </li>
              <li>
                <Link
                  to="/profile"
                  onClick={handleCloseOffcanvas}
                  className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                    location.pathname === "/profile"
                      ? "bg-neutral-100 text-neutral-900"
                      : "text-neutral-600 hover:bg-neutral-50"
                  }`}
                >
                  <FontAwesomeIcon icon={faUser} className="w-5 h-5 mr-3" />
                  Meu Perfil
                </Link>
              </li>
              {(userRole === "admin" || isSuperAdmin(user)) && (
                <li>
                  <Link
                    to="/recurring-admin"
                    onClick={handleCloseOffcanvas}
                    className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                      location.pathname === "/recurring-admin"
                        ? "bg-amber-100 text-amber-900"
                        : "text-amber-600 hover:bg-amber-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faRepeat} className="w-5 h-5 mr-3" />
                    Encomendas Recorrentes
                  </Link>
                </li>
              )}
              {userRole === "admin" && (
                <>
                  <li>
                    <Link
                      to="/create-user"
                      onClick={handleCloseOffcanvas}
                      className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                        location.pathname === "/create-user"
                          ? "bg-neutral-100 text-neutral-900"
                          : "text-neutral-600 hover:bg-neutral-50"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faUserPlus}
                        className="w-5 h-5 mr-3"
                      />
                      Gerir Utilizadores
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/clients"
                      onClick={handleCloseOffcanvas}
                      className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                        location.pathname === "/clients" || location.pathname.startsWith("/client")
                          ? "bg-neutral-100 text-neutral-900"
                          : "text-neutral-600 hover:bg-neutral-50"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faUsers}
                        className="w-5 h-5 mr-3"
                      />
                      Gerir Clientes
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/production-formulas"
                      onClick={handleCloseOffcanvas}
                      className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                        location.pathname === "/production-formulas"
                          ? "bg-neutral-100 text-neutral-900"
                          : "text-neutral-600 hover:bg-neutral-50"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faCalculator}
                        className="w-5 h-5 mr-3"
                      />
                      Gerir Fórmulas
                    </Link>
                  </li>
                  {process.env.NODE_ENV === "development" && (
                    <li>
                      <Link
                        to="/dev"
                        onClick={handleCloseOffcanvas}
                        className={`flex items-center px-4 py-2 rounded-lg cursor-pointer ${
                          location.pathname === "/dev"
                            ? "bg-blue-100 text-blue-900"
                            : "text-blue-600 hover:bg-blue-50"
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={faTerminal}
                          className="w-5 h-5 mr-3"
                        />
                        Dev Console
                      </Link>
                    </li>
                  )}
                </>
              )}
            </ul>
          </nav>

          <div className="pt-2 px-4 pb-4 border-t border-neutral-200 mt-auto">
            <div className="flex items-center mb-3">
              <img
                src={
                  userProfile?.avatar_url ||
                  "https://api.dicebear.com/7.x/notionists/svg?seed=1"
                }
                alt="User Avatar"
                className="w-8 h-8 rounded-full mr-3 object-cover"
              />
              <div>
                <p className="text-sm">{user?.email}</p>
                <p className="text-xs text-neutral-500">
                  {userRole === "admin" ? "Admin" : "Supervisor"}
                </p>
              </div>
            </div>
            <div className="mb-3">
              <p className="text-xs text-neutral-500">
                Versão: {packageJson.version}
              </p>
            </div>
            <button
              onClick={() => {
                logout(); // Usa a função de logout do AuthContext
                navigate("/");
                handleCloseOffcanvas();
              }}
              className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
              Sair
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default Layout;
